import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Wall-art galleries only differ per-variant in their first slide + first
        // thumbnail (the frame composite). Update those in place and slide to the
        // first image, instead of replacing the whole gallery (which recreates it
        // at slide 0 with a hard jump). Falls through to the full replace when the
        // in-place path doesn't apply.
        if (await this.#tryWallArtInPlaceUpdate(newMediaGallery)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Wall-art only: morph the first slide + first thumbnail (the per-variant frame
   * composite) into the existing slideshow and animate to the first image, so a
   * Size/Frame change reads as a slide rather than a jump. Keeps the slideshow
   * element (and its scroll state / refs) alive by morphing only the slide/
   * thumbnail contents.
   *
   * @param {Element} newMediaGallery - The freshly rendered gallery from the fetch.
   * @returns {Promise<boolean>} True if handled in place; false to fall back to replace.
   */
  #tryWallArtInPlaceUpdate = async (newMediaGallery) => {
    if (!this.classList.contains('media-gallery--wall-art')) return false;

    const slideshow = this.slideshow;
    if (!slideshow) return false;

    const oldSlide = this.querySelector('slideshow-slide');
    const newSlide = newMediaGallery.querySelector('slideshow-slide');
    if (!oldSlide || !newSlide) return false;

    // Only take the in-place path when both first slides are the frame composite.
    // If the composite is present on one side but not the other (e.g. a variant
    // with no matching overlay), the slide's structure changes — fall back to the
    // standard full replace so nothing renders half-updated.
    const oldHasOverlay = !!oldSlide.querySelector('.frame-overlay-media');
    const newHasOverlay = !!newSlide.querySelector('.frame-overlay-media');
    if (!oldHasOverlay || !newHasOverlay) return false;

    // Update the first slide's contents (overlay image + --art-inset / --frame-ratio
    // / background + orientation class). childrenOnly keeps the <slideshow-slide>
    // element, preserving the slideshow's `slides[]` refs and scroll state.
    morph(oldSlide, newSlide);

    // Update the first thumbnail of each thumbnail strip to match.
    const newThumbStrips = newMediaGallery.querySelectorAll('.slideshow-controls__thumbnails');
    this.querySelectorAll('.slideshow-controls__thumbnails').forEach((oldStrip, i) => {
      const newStrip = newThumbStrips[i];
      if (!newStrip) return;
      const oldFirst = oldStrip.querySelector('.slideshow-controls__thumbnail');
      const newFirst = newStrip.querySelector('.slideshow-controls__thumbnail');
      if (oldFirst && newFirst) morph(oldFirst, newFirst);
    });

    // Wait for the new overlay image to decode before sliding, so we never animate
    // to a blank/half-loaded frame. Time out defensively so we never hang.
    const overlayImg = oldSlide.querySelector('.frame-overlay-media__overlay');
    if (overlayImg instanceof HTMLImageElement && !overlayImg.complete && typeof overlayImg.decode === 'function') {
      await Promise.race([
        overlayImg.decode().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 400)),
      ]);
    }

    if (!this.isConnected) return true;

    // Slide to the (now updated) first image. No-op when already on the first slide.
    slideshow.select(0, undefined, { animate: true });
    return true;
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
